// Waly, zazebienia i rozwiązywanie napedu.
//
// Odpowiedz na pytanie "jak ruch trafia na kolo?": WAL to jedno sztywne cialo
// obrotowe, wiec kolo i zebatka osadzone na tym samym wale obracaja sie razem.
//
// POJECIA
//  - WAL: os obrotu. Tworzy ja wspornik osi ('axle_mount') albo przekladnia
//    w obudowie (pole `coupling`), kazda przez swoje sockety 'axle'.
//  - ZAZEBIENIE: dwie zebatki na ROZNYCH walach, ktorych osie dzieli r1 + r2.
//    Kierunek sie odwraca (dlatego przekladnia zebata "cofa" naped).
//  - SILNIK: modul napedu ma socket 'axle' (wyjscie). Dolaczenie go do socketu
//    'axle' wspornika znaczy "ten silnik kreci tym walem".
//
// Czysta domena: bez Plancka i bez DOM.

import { getDefinition } from './catalog';
import { socketWorldGrid } from './geometry';
import type { Connection, PartDefinition, RobotBlueprint, Vec2Like } from './types';

/** Tolerancja dopasowania osi przy zazebieniu (jednostki klockowe). */
const MESH_EPS = 1e-6;

export interface Shaft {
  /** Identyfikator = holderPartId#socketId (reprezentant grupy wspolosiowej). */
  id: string;
  holderPartId: string;
  holderSocketId: string;
  /** Czesci osadzone na wale (kola, zebatki, sprockety). Bez silnikow. */
  memberPartIds: string[];
  /**
   * Wszystkie uchwyty wspolosiowe tego walu (np. 'out' silnika i 'in' skrzyni).
   * Dwa sockety 'axle' w tym samym punkcie to JEDEN wal - dlatego silnik mozna
   * wprost spiac ze skrzynia, a nie tylko przez zebatki.
   */
  holders: { partId: string; socketId: string }[];
  /** Srodek osi w jednostkach klockowych. */
  grid: Vec2Like;
}

export interface MotorLink {
  drivePartId: string;
  shaftId: string;
}

export interface ShaftLink {
  kind: 'mesh' | 'chain' | 'coupling';
  fromShaftId: string;
  toShaftId: string;
  /** Mnoznik momentu: moment_na_wyjsciu = moment_wejsciowy * ratio. */
  ratio: number;
  /** +1 = ten sam kierunek, -1 = odwrocony. */
  direction: 1 | -1;
  /** Czesci tworzace sprzezenie. */
  partIds: string[];
}

export interface ShaftResolution {
  /** Mnoznik momentu wzgledem silnika. */
  ratio: number;
  direction: 1 | -1;
  /** Liczba stopni przekladni po drodze (do strat). */
  stages: number;
  drivePartId: string;
}

export interface Drivetrain {
  shafts: Shaft[];
  links: ShaftLink[];
  motors: MotorLink[];
  /** Waly z czesciami, ale nieosiagalne od zadnego silnika. */
  unpoweredShaftIds: string[];
  /** Zamkniete obwody przekladni (obecnosc = konstrukcja sie zacina). */
  cycles: string[][];
  /** Przelorzenia i kierunki wzgledem silnika, per wal. */
  ratios: Map<string, ShaftResolution>;
}

export function shaftIdOf(partId: string, socketId: string): string {
  return `${partId}#${socketId}`;
}

function definitionOf(blueprint: RobotBlueprint, partId: string): PartDefinition | null {
  const part = blueprint.parts.find((entry) => entry.id === partId);
  return part ? getDefinition(part.typeId) : null;
}

function isAxleSocket(def: PartDefinition, socketId: string): boolean {
  return def.sockets.some((socket) => socket.id === socketId && socket.kind === 'axle');
}

function holdsShafts(def: PartDefinition): boolean {
  // Wal tworzy wspornik osi, silnik (jego wlasny wal wyjsciowy) albo
  // przekladnia w obudowie (dwa swoje waly).
  return def.category === 'mount' || def.category === 'drive' || def.coupling !== undefined;
}

function pairs(conn: Connection): [Connection['a'], Connection['b']][] {
  return [
    [conn.a, conn.b],
    [conn.b, conn.a],
  ];
}

/** Waly konstrukcji wraz z czesciami na nich osadzonymi (wspolosiowe scalone). */
export function collectShafts(blueprint: RobotBlueprint): Shaft[] {
  // 1. Kandydaci: kazdy uchwyt walu (mount/drive/obudowa) z socketem 'axle'.
  const raw = new Map<string, { partId: string; socketId: string; grid: Vec2Like }>();
  for (const part of blueprint.parts) {
    const def = getDefinition(part.typeId);
    if (!holdsShafts(def)) continue;
    for (const socket of def.sockets) {
      if (socket.kind !== 'axle') continue;
      const id = shaftIdOf(part.id, socket.id);
      raw.set(id, { partId: part.id, socketId: socket.id, grid: socketWorldGrid(part, def, socket.id) });
    }
  }

  // 2. Union-find: dwa sockety 'axle' zlaczone ze soba to ten sam fizyczny wal.
  const parent = new Map<string, string>();
  for (const id of raw.keys()) parent.set(id, id);
  const find = (id: string): string => {
    let root = id;
    while (parent.get(root) !== root) root = parent.get(root) as string;
    let cur = id;
    while (parent.get(cur) !== root) {
      const next = parent.get(cur) as string;
      parent.set(cur, root);
      cur = next;
    }
    return root;
  };
  const union = (a: string, b: string): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  // 3. Czlonkowie per uchwyt (rozstrzygani na koncu, gdy grupy sa juz scalone).
  const membersOf = new Map<string, string[]>();

  for (const conn of blueprint.connections) {
    for (const [side, other] of pairs(conn)) {
      const sideId = shaftIdOf(side.partId, side.socketId);
      if (!raw.has(sideId)) continue;
      const otherId = shaftIdOf(other.partId, other.socketId);
      if (raw.has(otherId)) {
        // Oba konce to uchwyty walu - wspolosiowe, wiec jeden wal.
        union(sideId, otherId);
        continue;
      }
      const memberDef = definitionOf(blueprint, other.partId);
      if (!memberDef) continue;
      // Silnik nie obraca sie z walem - on go kreci (patrz collectMotors).
      if (memberDef.category === 'drive') continue;
      if (!isAxleSocket(memberDef, other.socketId)) continue;
      const list = membersOf.get(sideId) ?? [];
      if (!list.includes(other.partId)) list.push(other.partId);
      membersOf.set(sideId, list);
    }
  }

  // 4. Zbuduj scalone waly.
  const groups = new Map<string, Shaft>();
  for (const [id, info] of raw) {
    const root = find(id);
    let shaft = groups.get(root);
    if (!shaft) {
      shaft = {
        id: root,
        holderPartId: info.partId,
        holderSocketId: info.socketId,
        memberPartIds: [],
        holders: [],
        grid: info.grid,
      };
      groups.set(root, shaft);
    }
    if (!shaft.holders.some((holder) => holder.partId === info.partId && holder.socketId === info.socketId)) {
      shaft.holders.push({ partId: info.partId, socketId: info.socketId });
    }
  }

  for (const [sideId, partIds] of membersOf) {
    const shaft = groups.get(find(sideId));
    if (!shaft) continue;
    for (const partId of partIds) {
      if (!shaft.memberPartIds.includes(partId)) shaft.memberPartIds.push(partId);
    }
  }

  return [...groups.values()];
}

/**
 * Silniki: modul napedu kreci WLASNYM walem wyjsciowym (socket 'out'), tak jak
 * w prawdziwej maszynie wal silnika niesie zebatke albo sprocket. Nie trzeba
 * zadnego dodatkowego zlacza - obudowa silnika jest sztywna, a wal sie obraca.
 */
export function collectMotors(blueprint: RobotBlueprint, shafts: readonly Shaft[]): MotorLink[] {
  const motors: MotorLink[] = [];

  for (const shaft of shafts) {
    for (const holder of shaft.holders) {
      const def = definitionOf(blueprint, holder.partId);
      if (def?.category !== 'drive') continue;
      motors.push({ drivePartId: holder.partId, shaftId: shaft.id });
      break;
    }
  }

  return motors;
}

/** Zazebienia: zebatki na roznych walach, ktorych osie dzieli r1 + r2. */
export function collectMeshes(blueprint: RobotBlueprint, shafts: readonly Shaft[]): ShaftLink[] {
  const links: ShaftLink[] = [];
  const gears: { shaftId: string; partId: string; radius: number; grid: Vec2Like }[] = [];

  for (const shaft of shafts) {
    for (const memberId of shaft.memberPartIds) {
      const rotor = definitionOf(blueprint, memberId)?.rotor;
      if (!rotor || rotor.kind !== 'gear') continue;
      gears.push({
        shaftId: shaft.id,
        partId: memberId,
        radius: rotor.pitchRadiusGrid,
        grid: shaft.grid,
      });
    }
  }

  for (let i = 0; i < gears.length; i += 1) {
    for (let j = i + 1; j < gears.length; j += 1) {
      const a = gears[i];
      const b = gears[j];
      if (!a || !b) continue;
      if (a.shaftId === b.shaftId) continue; // jedna os to nie zazebienie
      const distance = Math.hypot(a.grid.x - b.grid.x, a.grid.y - b.grid.y);
      if (Math.abs(distance - (a.radius + b.radius)) > MESH_EPS) continue;
      links.push({
        kind: 'mesh',
        fromShaftId: a.shaftId,
        toShaftId: b.shaftId,
        // Wieksze kolo na wyjsciu daje wiekszy moment i mniejsza predkosc.
        ratio: b.radius / a.radius,
        // Zebatki obracaja sie w przeciwne strony - dlatego przekladnia "cofa".
        direction: -1,
        partIds: [a.partId, b.partId],
      });
    }
  }

  // Dwie zebatki na jednej osi moga siegac tej samej zebatki na innej osi - to
  // sprzezenie nadmiarowe (i geometrycznie musi miec to samo przelozenie).
  // Zostawiamy jedno na pare walow, zeby nie udawalo zamknietej petli.
  const unique = new Map<string, ShaftLink>();
  for (const link of links) {
    const key = [link.fromShaftId, link.toShaftId].sort().join('|');
    if (!unique.has(key)) unique.set(key, link);
  }

  return [...unique.values()];
}

/** Sprzezenia wewnatrz czesci: przekladnia w obudowie laczy dwa swoje waly. */
export function collectCouplings(
  blueprint: RobotBlueprint,
  shafts: readonly Shaft[],
): ShaftLink[] {
  const links: ShaftLink[] = [];
  const socketToShaft = new Map<string, string>();
  for (const shaft of shafts) {
    for (const holder of shaft.holders) {
      socketToShaft.set(shaftIdOf(holder.partId, holder.socketId), shaft.id);
    }
  }

  for (const part of blueprint.parts) {
    const coupling = definitionOf(blueprint, part.id)?.coupling;
    if (!coupling) continue;
    const fromId = socketToShaft.get(shaftIdOf(part.id, coupling.fromSocketId));
    const toId = socketToShaft.get(shaftIdOf(part.id, coupling.toSocketId));
    if (!fromId || !toId || fromId === toId) continue;
    links.push({
      // Zazebienie w obudowie to 'coupling', lancuch ma wlasny rodzaj 'chain' -
      // dzieki temu od razu widac, co przenosi naped i czy odwraca kierunek.
      kind: coupling.kind === 'chain' ? 'chain' : 'coupling',
      fromShaftId: fromId,
      toShaftId: toId,
      ratio: coupling.ratio,
      direction: coupling.kind === 'mesh' ? -1 : 1,
      partIds: [part.id],
    });
  }

  return links;
}

interface Edge {
  to: string;
  ratio: number;
  direction: 1 | -1;
}

/** Graf nieskierowany: przejscie w druga strone odwraca przelozenie, nie kierunek. */
function buildAdjacency(links: readonly ShaftLink[]): Map<string, Edge[]> {
  const adjacency = new Map<string, Edge[]>();
  const push = (from: string, to: string, ratio: number, direction: 1 | -1): void => {
    const list = adjacency.get(from) ?? [];
    list.push({ to, ratio, direction });
    adjacency.set(from, list);
  };

  for (const link of links) {
    push(link.fromShaftId, link.toShaftId, link.ratio, link.direction);
    push(link.toShaftId, link.fromShaftId, 1 / link.ratio, link.direction);
  }
  return adjacency;
}

/** Zamkniete obwody przekladni. Wystarczy znalezc jeden na obszar. */
function findCycles(shaftIds: readonly string[], adjacency: Map<string, Edge[]>): string[][] {
  const visited = new Set<string>();
  const found = new Map<string, string[]>();

  for (const start of shaftIds) {
    if (visited.has(start)) continue;
    const stack: { id: string; parent: string | null; path: string[] }[] = [
      { id: start, parent: null, path: [] },
    ];

    while (stack.length > 0) {
      const current = stack.pop();
      if (!current || visited.has(current.id)) continue;
      visited.add(current.id);
      const path = [...current.path, current.id];

      for (const edge of adjacency.get(current.id) ?? []) {
        // Powrot po tej samej krawedzi to nie obwod - graf jest nieskierowany.
        if (edge.to === current.parent || edge.to === current.id) continue;
        const index = path.indexOf(edge.to);
        if (index >= 0) {
          const cycle = path.slice(index);
          const key = [...cycle].sort().join('|');
          if (!found.has(key)) found.set(key, cycle);
          continue;
        }
        stack.push({ id: edge.to, parent: current.id, path });
      }
    }
  }

  return [...found.values()];
}

/**
 * Rozwiazuje naped: od kazdego silnika przechodzi graf i wylicza przelozenie
 * oraz kierunek dla kazdego walu. Bez obwodow graf jest drzewem, wiec wynik
 * jest jednoznaczny.
 */
export function resolveDrivetrain(blueprint: RobotBlueprint): Drivetrain {
  const shafts = collectShafts(blueprint);
  const motors = collectMotors(blueprint, shafts);
  const links = [...collectMeshes(blueprint, shafts), ...collectCouplings(blueprint, shafts)];
  const adjacency = buildAdjacency(links);
  const cycles = findCycles(
    shafts.map((shaft) => shaft.id),
    adjacency,
  );

  const ratios = new Map<string, ShaftResolution>();
  for (const motor of motors) {
    const queue: { shaftId: string; ratio: number; direction: 1 | -1; stages: number }[] = [
      { shaftId: motor.shaftId, ratio: 1, direction: 1, stages: 0 },
    ];

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current || ratios.has(current.shaftId)) continue;
      ratios.set(current.shaftId, {
        ratio: current.ratio,
        direction: current.direction,
        stages: current.stages,
        drivePartId: motor.drivePartId,
      });

      for (const edge of adjacency.get(current.shaftId) ?? []) {
        queue.push({
          shaftId: edge.to,
          ratio: current.ratio * edge.ratio,
          direction: (current.direction * edge.direction) as 1 | -1,
          stages: current.stages + 1,
        });
      }
    }
  }

  const unpoweredShaftIds = shafts
    .filter((shaft) => shaft.memberPartIds.length > 0 && !ratios.has(shaft.id))
    .map((shaft) => shaft.id);

  return { shafts, links, motors, unpoweredShaftIds, cycles, ratios };
}